/**
 * Профиль поиска (Прил. Е `SearchProfile`): пустой черновик и проверки,
 * зеркалящие проверки сервера. Сервер проверяет сам — здесь проверки нужны,
 * чтобы подсказать до отправки и не дать утвердить заведомо неполный или
 * дискриминирующий профиль.
 */
import type { KadrySearchProfile } from './types';

/** Верхняя граница «лет в профильной роли»: опыт сверх неё не учитывается (§1.6). */
export const KADRY_YEARS_IN_ROLE_MAX = 10;

/** Пустой профиль под заявку: должность — из названия, объект — из заявки. */
export function kadryBlankProfile(position: string, workSiteId?: string | null): KadrySearchProfile {
  return {
    position,
    synonyms: [],
    skills: { required: [], optional: [] },
    permits: { required: [] },
    experience: { years_in_role_min: null },
    work_site_id: workSiteId ?? null,
    schedule: null,
    readiness_required: { rotation: false, relocation: false, business_trips: false },
    pay: null,
    sources: ['internal'],
  };
}

export interface KadryProfileIssue {
  /** Поле профиля в нотации сервера (`details.field`): `position`, `skills.required`, … */
  field: string;
  message: string;
}

/**
 * Стоп-словарь §1.6: требования к полу, возрасту (и его суррогатам — стажу),
 * месту жительства, гражданству, семейному положению. Модуль их не использует
 * ни в поиске, ни в ранжировании, поэтому в свободные поля профиля они не
 * попадают. `(?<!\p{L})` — граница слова для кириллицы (`\b` в JS её не видит).
 */
const DISCRIMINATION_PATTERNS: readonly { re: RegExp; what: string }[] = [
  {
    re: /(?<!\p{L})(мужчин|женщин|муж\.|жен\.|мужск|женск|парн(и|ей|я|ям)(?!\p{L})|девушк|мужик|пол\s*[:–—-]?\s*[мж](?!\p{L}))/u,
    what: 'пол',
  },
  {
    re: /(?<!\p{L})(возраст|молод|пенсион|студент|выпускник)|(?<!\p{L})(до|от|не\s+старше|не\s+моложе|старше|моложе)\s*\d{1,2}\s*(лет|года|год)(?!\p{L})|\d{2}\s*[–—-]\s*\d{2}\s*(лет|года)(?!\p{L})|(?<!\p{L})(стаж|опыт)\w*\s+(от|не\s+менее|более|свыше)\s*\d+/u,
    what: 'возраст или общий стаж',
  },
  {
    re: /(?<!\p{L})(пропис|пмж|прожива|житель|местн(ый|ые|ых|ая|ого|ым)(?!\p{L})|гражданств|граждан(е|ин|ка)\s|национальн|славян|иногородн)/u,
    what: 'место жительства или гражданство',
  },
  {
    re: /(?<!\p{L})(семейн|женат|замуж|холост|без\s+детей|с\s+детьми|дет(и|ей)(?!\p{L}))/u,
    what: 'семейное положение',
  },
];

/** Что из стоп-словаря §1.6 найдено в тексте (`пол`, `возраст…`), или `null`. */
export function kadryDiscriminationHit(text: string): string | null {
  const t = text.toLowerCase().replace(/ё/g, 'е');
  for (const p of DISCRIMINATION_PATTERNS) {
    if (p.re.test(t)) return p.what;
  }
  return null;
}

/** Подсказка под свободными полями профиля. */
export const KADRY_NON_DISCRIMINATION_HINT =
  'Не указывайте пол, возраст, стаж «от N лет», место жительства, гражданство и семейное положение — модуль их не учитывает (ТЗ §1.6). Опыт — только полем «лет в профильной роли».';

function checkList(
  out: KadryProfileIssue[],
  field: string,
  label: string,
  items: string[] | undefined,
  maxItems: number,
  maxLen: number,
) {
  const list = items ?? [];
  if (list.length > maxItems) out.push({ field, message: `${label}: не больше ${maxItems}` });
  if (list.some((s) => s.trim().length > maxLen)) {
    out.push({ field, message: `${label}: элемент длиннее ${maxLen} символов` });
  }
  checkText(out, field, label, list.join(' · '));
}

function checkText(out: KadryProfileIssue[], field: string, label: string, text: string) {
  const hit = kadryDiscriminationHit(text);
  if (hit) {
    out.push({
      field,
      message: `${label}: похоже на требование про ${hit} — такие требования запрещены (§1.6)`,
    });
  }
}

/** Проблемы профиля, из-за которых сервер не утвердит версию. */
export function kadryProfileIssues(p: KadrySearchProfile): KadryProfileIssue[] {
  const out: KadryProfileIssue[] = [];
  const pos = p.position.trim();
  if (pos.length < 2 || pos.length > 200) {
    out.push({ field: 'position', message: 'Должность: от 2 до 200 символов' });
  }
  checkText(out, 'position', 'Должность', pos);
  checkList(out, 'synonyms', 'Синонимы', p.synonyms, 30, 120);
  checkList(out, 'skills.required', 'Обязательные навыки', p.skills?.required, 50, 120);
  checkList(out, 'skills.optional', 'Желательные навыки', p.skills?.optional, 50, 120);
  checkList(out, 'permits.required', 'Допуски', p.permits?.required, 30, 160);
  const years = p.experience?.years_in_role_min;
  if (years != null && (!Number.isFinite(years) || years < 0 || years > KADRY_YEARS_IN_ROLE_MAX)) {
    out.push({
      field: 'experience.years_in_role_min',
      message: `Лет в профильной роли: от 0 до ${KADRY_YEARS_IN_ROLE_MAX} — опыт сверх ${KADRY_YEARS_IN_ROLE_MAX} лет не учитывается (§1.6)`,
    });
  }
  const s = p.schedule;
  if (s) {
    if (s.shift != null) {
      if (s.shift.trim().length < 1 || s.shift.trim().length > 60) {
        out.push({ field: 'schedule.shift', message: 'Смена: от 1 до 60 символов' });
      }
      checkText(out, 'schedule.shift', 'Смена', s.shift);
    }
    if (s.rotation_days != null && (s.rotation_days < 1 || s.rotation_days > 120)) {
      out.push({ field: 'schedule.rotation_days', message: 'Вахта: от 1 до 120 дней' });
    }
  }
  const pay = p.pay;
  if (pay) {
    for (const v of [pay.from, pay.to]) {
      if (v != null && (v < 0 || v > 10_000_000)) {
        out.push({ field: 'pay', message: 'Оплата: от 0 до 10 000 000 ₽' });
        break;
      }
    }
    if (pay.from != null && pay.to != null && pay.from > pay.to) {
      out.push({ field: 'pay', message: 'Оплата: нижняя граница больше верхней' });
    }
  }
  if (!p.sources || p.sources.length === 0) {
    out.push({ field: 'sources', message: 'Выберите хотя бы один источник' });
  }
  return out;
}
